/**
 * sentry.js — Sentry initialisation for the React frontend.
 *
 * Call `initSentry()` once at the very top of main.jsx, before rendering,
 * so all rendering errors and async rejections are captured from the start.
 *
 * Sensitive data policy
 * ─────────────────────
 * • Session Replay masks ALL text and blocks ALL media by default so no
 *   user input (OTPs, card details, etc.) is ever recorded visually.
 * • Authorization and Cookie headers are stripped from every captured event.
 * • VITE_SENTRY_DSN must be set in .env; Sentry is silently disabled when absent.
 */

import * as Sentry from "@sentry/react";

export function initSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: `delhi-motors-frontend@${import.meta.env.VITE_APP_VERSION || "1.0.0"}`,

    integrations: [
      // Automatic browser performance tracing
      Sentry.browserTracingIntegration(),

      // Session Replay — records sessions so you can reproduce errors.
      // All text is masked and all media is blocked to protect user PII.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // 100% sampling at launch to catch all errors during initial traffic.
    // Tune down to 0.1 once steady-state traffic exceeds ~1,000 sessions/hr.
    tracesSampleRate: 1.0,

    // Record 10% of sessions; escalate to 100% when an error occurs
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event) {
      // Strip auth/session headers that may be present in XHR breadcrumbs
      if (event.request?.headers) {
        delete event.request.headers["authorization"];
        delete event.request.headers["cookie"];
      }
      return event;
    },
  });
}

export { Sentry };
